import React, { createContext } from 'react'
import { useKeycloak } from '@react-keycloak/web'
import UserServices from './Services/Consumers/UserServices'
import { useIntl } from 'react-intl'
import { NamedRoutes } from './routes'
import { useHistory } from 'react-router-dom'
import SubscriptionServices from './Services/Consumers/SubscriptionServices'

const RoleContext = createContext({})

const RoleProvider = ({ children, pageAction, rid, adminSectionType, handleNotificationCall }) => {
  const { keycloak } = useKeycloak()
  const intl = useIntl()
  const history = useHistory()
  const [deleteRoleLoading, setDeleteRoleLoading] = React.useState(false)
  const [roleDataLoading, setRoleDataLoading] = React.useState(false)
  const [roleDataFailure, setRoleDataFailure] = React.useState(false)
  const [roleData, setRoleData] = React.useState({})
  const [permissionsList, setPermissionsList] = React.useState([])
  const [permissionsListLoading, setPermissionsListLoading] = React.useState(false)

  React.useEffect(() => {
      if ('delete' !== pageAction) {
        getPermissionsList()
      }
      if (!!rid && !!parseInt(rid)) {
        getRoleData()
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [])

  const getPermissionsList = () => {
    if (!!keycloak?.authenticated && !permissionsList.length) {
      setPermissionsListLoading(true);
      ('subscription' === adminSectionType ? SubscriptionServices : UserServices).getPermissions({
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` },
        params: { 'page': 0, 'limit': 0 }
      }).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            setPermissionsList(result.data.data)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        setPermissionsListLoading(false)
      })
    }
  }
  const getRoleData = () => {
    if (!!keycloak?.authenticated) {
      setRoleDataFailure(false)
      setRoleDataLoading(true);
      ('subscription' === adminSectionType ? SubscriptionServices : UserServices).getRoleData(rid, { headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` } }).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          setRoleDataFailure(true)
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            let permissionsIds = (!!result.data.data.permissions && !!result.data.data.permissions.length) ?
              result.data.data.permissions.map(permission => permission.id) : []
            setRoleData({ ...result.data.data, 'permissionsIds': permissionsIds })
          } else {
            setRoleDataFailure(true)
          }
        }
      }).then(() => {
        setRoleDataLoading(false)
      })
    }
  }

  const handleRoleSubmit = (values, FormikActions) => {
    if (!!keycloak?.authenticated) {
      FormikActions.setSubmitting(true);
      ('subscription' === adminSectionType ? SubscriptionServices : UserServices).submitRole(rid, pageAction === 'edit', values, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }, FormikActions.setSubmitting, null, FormikActions).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          let message = intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' })
          if (result.response.data && result.response.data.status && result.response.data.status.errors) {
            message = result.response.data.status.errors
          }
          handleNotificationCall({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            handleNotificationCall({
              type: 'done',
              message: intl.formatMessage({ id: pageAction === 'edit' ? 'Role.Edit.FormSuccessAlert' : 'Role.Add.FormSuccessAlert' }),
              color: 'success'
            }, 'success')
            history.push(NamedRoutes[adminSectionType].RolesAndPermissions.list)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        FormikActions.setSubmitting(false)
      })
    }
  }

  const handleDeleteRole = (index, data, setData, count, setCount, handleDeleteRoleDialog) => {
    if (!!keycloak?.authenticated) {
      setDeleteRoleLoading(true);
      ('subscription' === adminSectionType ? SubscriptionServices : UserServices).deleteRole(data[index].id, {
        headers: { 'Authorization': `Bearer ${keycloak?.token ?? ''}` }
      }, setDeleteRoleLoading).then((result) => {
        if ((!!result.response && result.response.status !== 200) || !result) {
          handleNotificationCall({
            type: 'defence',
            message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
            color: 'error'
          }, 'error')
        } else {
          if (!!result && !!result.data && !!result.data.data) {
            handleNotificationCall({
              type: 'done',
              message: intl.formatMessage({ id: 'Role.Delete.FormSuccessAlert' }),
              color: 'success'
            }, 'success')
            let newData = data
            newData.splice(index, 1)
            setData(newData)
            setCount(--count)
          } else {
            handleNotificationCall({
              type: 'defence',
              message: intl.formatMessage({ id: 'BasicInfo.FormErrorAlert' }),
              color: 'error'
            }, 'error')
          }
        }
      }).then(() => {
        setDeleteRoleLoading(false)
        handleDeleteRoleDialog()
      })
    }
  }

  return (
    <RoleContext.Provider value={{
      roleDataLoading: roleDataLoading,
      roleDataFailure: roleDataFailure,
      roleData: roleData,
      pageAction: pageAction,

      permissionsList: permissionsList,
      permissionsListLoading: permissionsListLoading,

      handleRoleSubmit,

      deleteRoleLoading: deleteRoleLoading,
      handleDeleteRole
    }}>
      {children}
    </RoleContext.Provider>
  )
}
const RoleConsumer = RoleContext.Consumer

export { RoleProvider, RoleConsumer }
